/**
 * Delimited Format Converter Tool
 *
 * Generic pipeline transformer: converts between CSV ↔ TSV ↔ pipe ↔ semicolon.
 * Auto-detects input delimiter via shared detection, re-serializes with Papa.unparse.
 *
 * @see .squad/decisions/inbox/mal-table-architecture.md §2.5
 */

import * as Papa from 'papaparse';

import { SEMANTIC_TYPES } from '@/detectors/semanticTypes';
import { ToolError } from '@/engine/errors/ToolError';
import type { ISemanticType } from '@/interfaces/ISemanticType';
import type { ITransformerTool, ToolExecutionResult } from '@/interfaces/ITool';
import { detectTabularFormat } from '@/lib/tabularFormatDetection';

const OUTPUT_DELIMITERS: Record<string, string> = {
  csv: ',',
  tsv: '\t',
  pipe: '|',
  semicolon: ';',
};

const FORMAT_TO_SEMANTIC_TYPE: Record<string, ISemanticType> = {
  csv: SEMANTIC_TYPES.CSV,
  tsv: SEMANTIC_TYPES.TSV,
  pipe: SEMANTIC_TYPES.PIPE,
  semicolon: SEMANTIC_TYPES.CSV, // no dedicated semantic type; CSV is closest
};

export const DelimitedFormatConverterTool: ITransformerTool<string | ToolExecutionResult> = {
  id: 'delimited-format-converter',
  name: 'Delimited Format Converter',
  description: 'Convert between CSV, TSV, pipe-separated, and semicolon-separated formats',
  version: '1.0.0',
  type: 'transformer',
  category: 'Converters',
  keywords: ['csv', 'tsv', 'pipe', 'semicolon', 'delimited', 'convert', 'tabular'],

  supportedInputTypes: [SEMANTIC_TYPES.CSV, SEMANTIC_TYPES.TSV, SEMANTIC_TYPES.PIPE],
  outputType: SEMANTIC_TYPES.CSV,

  getOutputType(config: Record<string, unknown>): ISemanticType | undefined {
    const format = (config.outputFormat as string) ?? 'csv';
    return FORMAT_TO_SEMANTIC_TYPE[format];
  },

  attributes: [
    {
      name: 'outputFormat',
      type: 'select' as const,
      label: 'Output Format',
      defaultValue: 'csv',
      options: ['csv', 'tsv', 'pipe', 'semicolon'],
      description: 'Target delimited format',
    },
    {
      name: 'hasHeader',
      type: 'boolean' as const,
      label: 'Has Header Row',
      defaultValue: true,
      description: 'Treat the first row as column headers',
    },
    {
      name: 'quoteAll',
      type: 'boolean' as const,
      label: 'Quote All Fields',
      defaultValue: false,
      description: 'Wrap every field in quotes in the output',
    },
  ],

  supportsInput(_input: string, detectedType?: string): boolean {
    return detectedType === 'CSV' || detectedType === 'TSV' || detectedType === 'Pipe';
  },

  async transform(input: string, config: Record<string, unknown>): Promise<string | ToolExecutionResult> {
    try {
      const cleaned = input.replace(/^\uFEFF/, '');
      const outputFormat = (config.outputFormat as string) ?? 'csv';
      const quoteAll = config.quoteAll === true;

      const targetDelimiter = OUTPUT_DELIMITERS[outputFormat];
      if (!targetDelimiter) {
        throw ToolError.validation(`Unknown output format: ${outputFormat}`);
      }

      // Auto-detect input delimiter
      const formatInfo = detectTabularFormat(cleaned);
      const inputDelimiter = formatInfo?.delimiter ?? ',';

      // Parse with detected delimiter
      const result = Papa.parse<string[]>(cleaned, {
        delimiter: inputDelimiter,
        header: false,
        skipEmptyLines: false,
      });

      if (result.errors.length > 0 && result.data.length === 0) {
        throw ToolError.validation(result.errors[0]?.message ?? 'Failed to parse input');
      }

      const rows = result.data;

      // Check if all rows are empty
      const hasData = rows.some((row) => row.some((cell) => cell !== ''));
      if (!hasData) {
        throw ToolError.validation('No data rows found in input');
      }

      // Re-serialize with target delimiter, preserving blank rows
      const output = Papa.unparse(rows, {
        delimiter: targetDelimiter,
        quotes: quoteAll,
        newline: '\n',
      });

      return {
        output,
        outputType: FORMAT_TO_SEMANTIC_TYPE[outputFormat] ?? SEMANTIC_TYPES.CSV,
      };
    } catch (e) {
      const error = e as Error;
      if (error instanceof ToolError) throw error;
      throw ToolError.validation(`Delimited conversion failed: ${error.message}`, {
        suggestion: 'Check that input is valid delimited data with consistent columns',
      });
    }
  },
};
